import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Users, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  subscription: any | null;
}

const RenewSubscriptionDialog = ({ open, onOpenChange, subscription }: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState("monthly");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [pricingModel, setPricingModel] = useState<"fixed" | "per_child">("fixed");
  const [pricePerChild, setPricePerChild] = useState("10");
  const [minCharge, setMinCharge] = useState("500");

  // Sync defaults from subscription
  const subId = subscription?.id;
  const [lastSyncId, setLastSyncId] = useState<string | null>(null);
  if (subscription && subId !== lastSyncId) {
    setLastSyncId(subId);
    setPricingModel(subscription.pricing_model || "fixed");
    setPricePerChild(String(subscription.price_per_child || 10));
    setMinCharge(String(subscription.min_charge || 500));
    setCycle(subscription.billing_cycle || "monthly");
    setAmount(String(subscription.amount || ""));
  }

  const nurseryId = subscription?.nursery_id;

  const { data: childrenCount = 0 } = useQuery({
    queryKey: ["renew-sub-children-count", nurseryId],
    queryFn: async () => {
      if (!nurseryId) return 0;
      const { count } = await supabase
        .from("children")
        .select("*", { count: "exact", head: true })
        .eq("nursery_id", nurseryId);
      return count || 0;
    },
    enabled: !!nurseryId && pricingModel === "per_child",
  });

  const perChildAmount = useMemo(() => {
    if (pricingModel !== "per_child") return 0;
    const ppc = parseFloat(pricePerChild) || 0;
    const mc = parseFloat(minCharge) || 0;
    return Math.max(childrenCount * ppc, mc);
  }, [pricingModel, pricePerChild, minCharge, childrenCount]);

  if (!subscription) return null;

  const nurseryName = subscription.nurseries?.name || "—";

  const handleRenew = async () => {
    setLoading(true);
    const now = new Date();
    const expiresAt = cycle === "yearly"
      ? new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
      : new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());

    const finalAmount = pricingModel === "per_child" ? perChildAmount : (parseFloat(amount) || subscription.amount);

    const updateData: any = {
      status: "active",
      is_trial: false,
      trial_ends_at: null,
      billing_cycle: cycle,
      amount: finalAmount,
      expires_at: expiresAt.toISOString(),
      started_at: now.toISOString(),
      payment_method: paymentMethod,
      pricing_model: pricingModel,
    };

    if (pricingModel === "per_child") {
      updateData.price_per_child = parseFloat(pricePerChild) || 10;
      updateData.min_charge = parseFloat(minCharge) || 500;
    }

    const { error } = await supabase.from("nursery_subscriptions").update(updateData).eq("id", subscription.id);

    setLoading(false);
    if (error) { toast.error("خطأ: " + error.message); return; }
    toast.success("تم تجديد الاشتراك بنجاح ✅");
    queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-green-600" />
            تجديد اشتراك: {nurseryName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Pricing Model */}
          <div>
            <Label>نموذج التسعير</Label>
            <Select value={pricingModel} onValueChange={(v) => setPricingModel(v as "fixed" | "per_child")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">ثابت (باقة)</SelectItem>
                <SelectItem value="per_child">حسب عدد الأطفال</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {pricingModel === "fixed" ? (
            <div>
              <Label>المبلغ (ج.م)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`${subscription.amount}`} dir="ltr" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>سعر الطفل (ج.م)</Label>
                  <Input type="number" value={pricePerChild} onChange={(e) => setPricePerChild(e.target.value)} min={0} dir="ltr" />
                </div>
                <div>
                  <Label>الحد الأدنى (ج.م)</Label>
                  <Input type="number" value={minCharge} onChange={(e) => setMinCharge(e.target.value)} min={0} dir="ltr" />
                </div>
              </div>
              <div className="rounded-lg border p-3 bg-accent/30 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Calculator className="w-4 h-4" />
                  <span>حساب تلقائي</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{childrenCount} طفل × {pricePerChild} ج.م = {(childrenCount * (parseFloat(pricePerChild) || 0)).toLocaleString()} ج.م</span>
                </div>
                {perChildAmount > childrenCount * (parseFloat(pricePerChild) || 0) && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ تم تطبيق الحد الأدنى ({parseFloat(minCharge).toLocaleString()} ج.م)
                  </p>
                )}
                <div className="text-sm font-bold border-t pt-1">
                  المبلغ الشهري: {perChildAmount.toLocaleString()} ج.م
                </div>
              </div>
            </>
          )}

          <div>
            <Label>دورة التجديد</Label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">شهري</SelectItem>
                <SelectItem value="yearly">سنوي</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>طريقة الدفع</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                <SelectItem value="cash">نقدي</SelectItem>
                <SelectItem value="online">إلكتروني</SelectItem>
                <SelectItem value="card">بطاقة</SelectItem>
                <SelectItem value="wallet">محفظة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="w-full" onClick={handleRenew} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "تأكيد التجديد"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RenewSubscriptionDialog;
